// src/rms/data/counterLogin.js
// ============================================================================
// The shared counter login
// ============================================================================
// A name and a password the whole shift knows, so a casual can work the counter
// without the owner handing over their own login and without an account being
// made for somebody who leaves in September.
//
// Named staff accounts already exist and are better where they fit: they name
// the person in the roster and survive them changing shifts. This is for the
// case where making an account per person is more ceremony than the job needs.
//
// ----------------------------------------------------------------------------
// It is a real account, at staff level, and that is the whole safety story
// ----------------------------------------------------------------------------
// Supabase auth needs an email, so the name becomes one the operator never
// sees. What it is NOT is a special case in the security model: it gets an
// ordinary member row at `staff`, RLS treats it like any other staff member,
// and every consequential write behind it still needs a PIN that resolves to a
// named person.
//
// So the shared password buys entry to the counter's work and nothing else.
// Pricing, billing and settings are refused to it, and an owner standing at
// that terminal reaches them by typing their own PIN -- which rms_elevate
// resolves to THEM, not to the session.
//
// ----------------------------------------------------------------------------
// The one thing to keep in mind
// ----------------------------------------------------------------------------
// A shared password is known by everyone who ever worked there, including the
// people who left. Rotating it is one field in the same card that made it, and
// it should be used whenever somebody leaves.
// ============================================================================

/**
 * The synthetic address a counter name maps to.
 *
 * Computed on the CLIENT for both creating and signing in, so there is exactly
 * one implementation of the rule. The edge function is handed the result and
 * treats it as an ordinary address -- putting this logic there too would be two
 * encodings of one decision, which is the failure permissions.js documents and
 * which cost a whole debugging round earlier today.
 *
 * `.invalid` is reserved by RFC 2606 precisely so it can never be a real
 * domain. Mail to it cannot escape, cannot bounce against somebody else's
 * server, and cannot be confused for an address a person owns.
 */
export const COUNTER_DOMAIN = "counter.reservezoo.invalid";

/** Lowercase, letters and digits only. What is typed at sign-in, normalised. */
export function counterSlug(name) {
  return String(name || "")
    .toLowerCase()
    .normalize("NFKD")
    // Strip accents so "Café" and "Cafe" are the same login rather than two
    // that look identical on a card by the till.
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

/** The address for a counter name, or null when the name is unusable. */
export function counterEmail(name) {
  const slug = counterSlug(name);
  return slug.length >= 3 ? `${slug}@${COUNTER_DOMAIN}` : null;
}

/** True for anything typed into a sign-in box that is not an email address. */
export const looksLikeCounterName = (value) => !String(value || "").includes("@");

/**
 * What the sign-in screen should actually send to Supabase.
 *
 * An address goes through untouched; anything else is treated as a counter
 * name. That keeps one field for both, which is what somebody at a counter
 * expects -- they type what they were told and it works.
 */
export function resolveIdentifier(value) {
  const v = String(value || "").trim();
  return looksLikeCounterName(v) ? counterEmail(v) : v.toLowerCase();
}

export default { COUNTER_DOMAIN, counterSlug, counterEmail, looksLikeCounterName, resolveIdentifier };
